import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format, isAfter, parseISO, isValid } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Bell, CheckCircle, AlertCircle, Clock, Search, Sun, Moon } from 'lucide-react';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { PopoverContent, PopoverTrigger, PopoverRoot } from './components/ui/Popover';
import { useToast } from './CustomCalendarToast';
import CalendarTaskList from './CalendarTaskList';
import './CalendarTasks.css';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { Box, Typography, Checkbox, IconButton, LinearProgress, AppBar, Toolbar, Container } from '@mui/material';
import { Delete, Edit, AccessTime } from '@mui/icons-material';

const CalendarTasks = ({ selectedDate, quotes }) => {
  const [tasks, setTasks] = useState([]);
  const [newTask, setNewTask] = useState('');
  const [newTaskDate, setNewTaskDate] = useState(new Date());
  const { addToast } = useToast();
  const [darkMode, setDarkMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const darkTheme = createTheme({
    palette: {
      mode: darkMode ? 'dark' : 'light',
    },
  });

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const ensureValidDate = (date) => {
    if (date instanceof Date && !isNaN(date)) {
      return date;
    }
    if (typeof date === 'string') {
      const parsedDate = parseISO(date);
      return isValid(parsedDate) ? parsedDate : new Date();
    }
    return new Date();
  };

  useEffect(() => {
    const fetchTasks = async () => {
      const validSelectedDate = ensureValidDate(selectedDate);
      const fetchedTasks = [
        { id: 1, title: 'Relancer le client pour le devis #1234', dueDate: new Date(validSelectedDate.getTime() + 2 * 24 * 60 * 60 * 1000), status: 'pending', progress: 0, completed: false },
        { id: 2, title: 'Suivre le paiement du devis #5678', dueDate: new Date(validSelectedDate.getTime() + 5 * 24 * 60 * 60 * 1000), status: 'pending', progress: 0, completed: false },
      ];
      setTasks(fetchedTasks);
    };
    fetchTasks();
  }, [selectedDate]);

  const addTask = () => {
    if (newTask.trim() === '') return;
    const taskObj = {
      id: Date.now(),
      title: newTask,
      dueDate: ensureValidDate(newTaskDate),
      status: 'pending',
      progress: 0,
      completed: false
    };
    setTasks([...tasks, taskObj]);
    setNewTask('');
    setNewTaskDate(new Date());
    addToast("La nouvelle tâche a été ajoutée avec succès.", "success");
  };

  const completeTask = (taskId) => {
    setTasks(tasks.map(task =>
      task.id === taskId ? { ...task, status: 'completed', completed: true, progress: 100 } : task
    ));
    addToast("La tâche a été marquée comme terminée.", "success");
  };

  const deleteTask = (taskId) => {
    setTasks(tasks.filter(task => task.id !== taskId));
    addToast("La tâche a été supprimée avec succès.", "success");
  };

  const editTask = (taskId) => {
    console.log('Édition de la tâche:', taskId);
  };

  const formatDate = (date) => {
    const validDate = ensureValidDate(date);
    return format(validDate, 'dd/MM/yyyy', { locale: fr });
  };

  const filteredTasks = tasks.filter(task =>
    task.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <ThemeProvider theme={darkTheme}>
      <Box sx={{ flexGrow: 1 }}>
        <AppBar position="static" color={darkMode ? "default" : "primary"}>
          <Toolbar>
          <Typography
              variant="h6"
              component="div"
            sx={{
              flexGrow: 1,
              display: 'flex',
              alignItems: 'center',
              fontSize: '2.0rem',
              fontWeight: 'standard',
              color: '#fff', // Couleur du texte toujours blanche
              padding: '0.5rem 1rem',
              borderRadius: '0.5rem',
              backgroundColor: darkMode ? '#333' : 'primary.main', // Utilise la couleur primaire en mode clair
            }}
            >
              <Bell className="mr-2" />
               Rappels et Tâches
          </Typography>
          
            <Input
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder="Rechercher une tâche..."
              className="mr-2"
              style={{ width: '200px', color: '#333' }} 
            />
            <IconButton onClick={() => setDarkMode(!darkMode)} color="inherit">
              {darkMode ? <Sun /> : <Moon />}
            </IconButton>
          </Toolbar>
        </AppBar>
      </Box>
      
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className={`calendar-tasks p-4 rounded-lg shadow mt-4 ${darkMode ? 'bg-gray-800 text-white' : 'bg-gray-100'}`}
        >
          <div className="mb-4">
            <Input
              type="text"
              value={newTask}
              onChange={(e) => setNewTask(e.target.value)}
              placeholder="Nouvelle tâche..."
              className="mb-2"
              style={{ width: '100%', fontWeight: 'bold', fontSize: '1.2rem' }}
            />
            <div className="flex items-center space-x-2">
              <Input
                type="date"
                value={format(newTaskDate, 'yyyy-MM-dd')}
                onChange={(e) => setNewTaskDate(new Date(e.target.value))}
                style={{ fontSize: '1.2rem' }}
              />
              <Button
                onClick={addTask}
                style={{
                  fontSize: '1.2rem',
                  padding: '0.5rem 1rem',
                  height: '40px',
                }}
              >
                Ajouter
              </Button>
            </div>
          </div>
          <CalendarTaskList
            tasks={filteredTasks}
            onDeleteTask={deleteTask}
            onEditTask={editTask}
            onCompleteTask={completeTask}
            formatDate={formatDate}
          />
        </motion.div>
      </Container>
    </ThemeProvider>
  );
};

export default CalendarTasks;